import { MapaRotaMotorista } from '@sotran/lib-shipper';
import { notification, Pagination } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import qs from 'qs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFiltro } from '../../../../contexts/FilaEmbarque/Filtro/hooks/useFiltro';
import { capitalizar } from '../../../../helpers/capitalizar';
import { useAuth } from '../../../../security/authContext';
import apiNetCore from '../../../../services/apiNetCore';
import apiShipper from '../../../../services/apiShipper';
import StatusMotoristaEnum from '../../Enum/StatusMotoristaEnum';
import FiltroData from './FiltroData';
import { Container } from './styles';
import { TabelaMotoristas } from './TabelaMotoristas';
import Totalizadores from './Totalizadores';

const FilaEmbarque = ({ detalhesDemanda, idLotes, origem, destino }) => {
  const [loadingMotoristas, setLoadingMotoristas] = useState(false);
  const [totalizadores, setTotalizadores] = useState();
  const [motoristas, setMotoristas] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [take, setTake] = useState(50);
  const [total, setTotal] = useState(0);
  const [anexoNfe, setAnexoNfe] = useState(false);
  const { filtro, fetchFiltro } = useFiltro();
  const { user } = useAuth();

  const { dataInicial, dataFinal, filtrosDrawer } = filtro;

  const loadDadosCliente = async () => {
    await apiShipper
      .get(`/clientes/${user.cliente_id}`)
      .then(({ data }) => {
        setAnexoNfe(data?.anexo_nfe);
      })
      .catch(() => {
        notification.error({
          message: 'Erro ao carregar cliente',
          description:
            'Caso o problema persista, entre em contato com nosso suporte.',
        });
      });
  };

  useEffect(() => {
    (async () => {
      if (!idLotes.length || !filtro.fetchFilterData) return;
      await loadDadosCliente();
    })();
  }, []);

  const loadMotoristas = () => {
    setLoadingMotoristas(true);
    apiNetCore
      .get('/LoteConjuntoAts/GetFilaEmbarque', {
        params: {
          page: currentPage,
          size: take,
          dataInicio: dataInicial,
          dataFim: dataFinal,
          ...filtrosDrawer,
          idLote: idLotes,
        },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      })
      .then(({ data }) => {
        setMotoristas(data.items);
        setTotalizadores(data);
        setTotal(data.totalItems);
      })
      .catch(() => {
        notification.error({
          message: 'Erro ao carregar motoristas',
          description:
            'Caso o problema persista, entre em contato com nosso suporte.',
        });
      })
      .finally(() => setLoadingMotoristas(false));
  };

  const motoristaMapa = useMemo(() => {
    const result =
      motoristas &&
      motoristas
        .filter(
          motorista =>
            motorista.Status !==
            StatusMotoristaEnum.getTipo(StatusMotoristaEnum.REMOVIDO).label
        )
        .map(motorista => ({
          nome: motorista.Nome,
          placa: motorista.PlacaCavalo,
          status: motorista.Status,
          position: motorista.LatitudeCheckin &&
            motorista.LongitudeCheckin && {
              latitude: motorista.LatitudeCheckin,
              longitude: motorista.LongitudeCheckin,
            },
          ultimaAtualizacao:
            motorista.UltimoCheckin &&
            moment(motorista.UltimoCheckin).format('DD/MM/YYYY HH:mm:ss'),
        }));
    return result;
  }, [motoristas]);

  useEffect(() => {
    (async () => {
      if (!idLotes.length || !filtro.fetchFilterData) return;

      loadMotoristas();

      fetchFiltro(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, take, filtro.fetchFilterData]);

  const onPageChange = useCallback(
    (page, pageSize) => {
      fetchFiltro(true);
      setCurrentPage(page);
      setTake(size => pageSize || size);
    },
    [fetchFiltro]
  );

  const isGeoLocalizacao = () =>
    origem.latitudeCidade &&
    origem.longitudeCidade &&
    destino.latitudeCidade &&
    destino.longitudeCidade;

  return (
    <Container>
      <FiltroData />
      <Totalizadores totalizadores={totalizadores} />
      {isGeoLocalizacao() && (
        <MapaRotaMotorista
          loading={loadingMotoristas}
          isUsuarioSotran={false}
          mapHeight="40vh"
          startPosition={{
            latitude: origem.latitudeCidade,
            longitude: origem.longitudeCidade,
            address: capitalizar(origem.cidade),
          }}
          endPosition={{
            latitude: destino.latitudeCidade,
            longitude: destino.longitudeCidade,
            address: capitalizar(destino.cidade),
          }}
          motoristas={motoristaMapa}
        />
      )}
      <TabelaMotoristas
        detalhesDemanda={detalhesDemanda}
        motoristas={motoristas}
        setMotoristas={setMotoristas}
        loading={loadingMotoristas}
        loadMotoristas={loadMotoristas}
        anexoNfe={anexoNfe}
      />
      <Pagination
        showSizeChanger
        defaultCurrent={currentPage}
        current={currentPage}
        total={total}
        pageSize={take}
        pageSizeOptions={[10, 20, 50]}
        onChange={onPageChange}
      />
    </Container>
  );
};

const endereco = PropTypes.shape({
  bairro: PropTypes.string,
  cep: PropTypes.string,
  cidade: PropTypes.string,
  cnpj: PropTypes.string,
  complemento: PropTypes.string,
  endereco: PropTypes.string,
  estado: PropTypes.string,
  ibgeCidade: PropTypes.number,
  idCidade: PropTypes.number,
  idCliente: PropTypes.number,
  idClienteEndereco: PropTypes.number,
  idEstado: PropTypes.number,
  nomeCliente: PropTypes.string,
  numero: PropTypes.number,
  latitudeCidade: PropTypes.number,
  longitudeCidade: PropTypes.number,
});

FilaEmbarque.propTypes = {
  detalhesDemanda: PropTypes.shape({
    enderecoDestino: PropTypes.arrayOf(endereco),
    enderecoExpedidor: PropTypes.arrayOf(endereco),
    enderecoOrigem: PropTypes.arrayOf(endereco),
    enderecoRecebedor: PropTypes.arrayOf(endereco),
    razaoSocialClientePagador: PropTypes.string,
    latitudeCidade: PropTypes.number,
    longitudeCidade: PropTypes.number,
  }),
  idLotes: PropTypes.arrayOf(PropTypes.number),
  origem: endereco.isRequired,
  destino: endereco.isRequired,
};

FilaEmbarque.defaultProps = {
  detalhesDemanda: null,
  idLotes: [],
};

export default FilaEmbarque;
